// 지도 모양 도우미(S2, docs/STAGE-WORLD-PRD.md 2.1): 원·고리·길·타원 같은 모양을 1m 격자 사각형 목록(Rect[])으로 바꾼다.
// 판정은 늘 1m 격자라 모양이 곧 '칸 집합'이다. 모든 함수는 순수 함수이고 결과는 겹침 없는 행 단위 사각형(세로로 같은 폭은 합친다)이다.
// 칸 (cx, cz)는 [cx, cx+1) × [cz, cz+1) 정사각형이고, 칸 중심(cx + 0.5, cz + 0.5)이 모양 안에 있으면 포함된다.
import type { Rect } from './levels';

const rc = (x0: number, z0: number, x1: number, z1: number): Rect => ({ x0, z0, x1, z1 });

/** 칸 집합 → 겹침 없는 사각형. 한 행의 연속 구간을 만들고, 이어지는 행에서 폭이 같으면 세로로 합친다 */
export function rectsOfCells(cells: ReadonlySet<string>): Rect[] {
  const rows = new Map<number, number[]>();
  for (const key of cells) {
    const [x, z] = key.split(',').map(Number);
    let r = rows.get(z);
    if (!r) rows.set(z, (r = []));
    r.push(x);
  }
  const zs = [...rows.keys()].sort((a, b) => a - b);
  // 진행 중인 사각형: "x0:x1" → { x0, x1, z0, z1 }
  let open = new Map<string, Rect>();
  const out: Rect[] = [];
  let prevZ = Number.NaN;
  for (const z of zs) {
    const xs = rows.get(z)!.sort((a, b) => a - b);
    const next = new Map<string, Rect>();
    for (let i = 0; i < xs.length;) {
      let j = i;
      while (j + 1 < xs.length && xs[j + 1] === xs[j] + 1) j++;
      const x0 = xs[i], x1 = xs[j] + 1, key = x0 + ':' + x1;
      const cur = prevZ === z - 1 ? open.get(key) : undefined;
      if (cur) { open.delete(key); next.set(key, rc(cur.x0, cur.z0, cur.x1, z + 1)); } else next.set(key, rc(x0, z, x1, z + 1));
      i = j + 1;
    }
    for (const r of open.values()) out.push(r);
    open = next;
    prevZ = z;
  }
  for (const r of open.values()) out.push(r);
  return out.sort((a, b) => a.z0 - b.z0 || a.x0 - b.x0);
}

/** 사각형 목록 → 칸 집합 */
export function cellsOf(rects: readonly Rect[]): Set<string> {
  const s = new Set<string>();
  for (const r of rects) for (let z = r.z0; z < r.z1; z++) for (let x = r.x0; x < r.x1; x++) s.add(x + ',' + z);
  return s;
}

/** 칸 중심이 inside를 만족하는 칸들(bbox 안) */
export function fromPredicate(x0: number, z0: number, x1: number, z1: number, inside: (x: number, z: number) => boolean): Rect[] {
  const s = new Set<string>();
  for (let z = Math.floor(z0); z < Math.ceil(z1); z++) for (let x = Math.floor(x0); x < Math.ceil(x1); x++) if (inside(x + 0.5, z + 0.5)) s.add(x + ',' + z);
  return rectsOfCells(s);
}

/** 원판 */
export function disk(cx: number, cz: number, r: number): Rect[] {
  return fromPredicate(cx - r, cz - r, cx + r, cz + r, (x, z) => (x - cx) ** 2 + (z - cz) ** 2 <= r * r);
}
/** 고리(안쪽 반지름 r0, 바깥 r1) */
export function ring(cx: number, cz: number, r0: number, r1: number): Rect[] {
  return fromPredicate(cx - r1, cz - r1, cx + r1, cz + r1, (x, z) => {
    const d2 = (x - cx) ** 2 + (z - cz) ** 2;
    return d2 <= r1 * r1 && d2 >= r0 * r0;
  });
}
/** 타원판(가로 반지름 rx, 세로 반지름 rz) */
export function ellipse(cx: number, cz: number, rx: number, rz: number): Rect[] {
  return fromPredicate(cx - rx, cz - rz, cx + rx, cz + rz, (x, z) => ((x - cx) / rx) ** 2 + ((z - cz) / rz) ** 2 <= 1);
}
/** 점 열을 따라가는 길(폭 width, 끝은 둥글다) */
export function path(points: readonly (readonly [number, number])[], width: number): Rect[] {
  const h = width / 2;
  let minX = Infinity, minZ = Infinity, maxX = -Infinity, maxZ = -Infinity;
  for (const [x, z] of points) { minX = Math.min(minX, x); minZ = Math.min(minZ, z); maxX = Math.max(maxX, x); maxZ = Math.max(maxZ, z); }
  const dist2 = (px: number, pz: number, ax: number, az: number, bx: number, bz: number): number => {
    const dx = bx - ax, dz = bz - az, l2 = dx * dx + dz * dz;
    const t = l2 === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (pz - az) * dz) / l2));
    return (px - ax - dx * t) ** 2 + (pz - az - dz * t) ** 2;
  };
  return fromPredicate(minX - h, minZ - h, maxX + h, maxZ + h, (x, z) => {
    for (let i = 0; i + 1 < points.length; i++) if (dist2(x, z, points[i][0], points[i][1], points[i + 1][0], points[i + 1][1]) <= h * h) return true;
    return points.length === 1 && (x - points[0][0]) ** 2 + (z - points[0][1]) ** 2 <= h * h;
  });
}
/** 모서리를 비스듬히 자른 사각형. corners = 자를 모서리('nw'|'ne'|'sw'|'se'), cut = 자르는 변 길이(m) */
export function chamfer(r: Rect, cut: number, corners: readonly ('nw' | 'ne' | 'sw' | 'se')[]): Rect[] {
  return fromPredicate(r.x0, r.z0, r.x1, r.z1, (x, z) => {
    for (const c of corners) {
      const ex = c.endsWith('w') ? x - r.x0 : r.x1 - x, ez = c.startsWith('n') ? z - r.z0 : r.z1 - z;
      if (ex + ez < cut) return false;
    }
    return true;
  });
}

type Shape = Rect | readonly Rect[];
const listOf = (s: Shape): readonly Rect[] => ('x0' in s ? [s] : s);

/** 합집합(겹침 없는 사각형). 인자는 사각형 하나 또는 목록 */
export function union(...shapes: readonly Shape[]): Rect[] {
  const s = new Set<string>();
  for (const l of shapes) for (const k of cellsOf(listOf(l))) s.add(k);
  return rectsOfCells(s);
}
/** 차집합 a − b */
export function subtract(a: Shape, ...cut: readonly Shape[]): Rect[] {
  const s = cellsOf(listOf(a));
  for (const l of cut) for (const k of cellsOf(listOf(l))) s.delete(k);
  return rectsOfCells(s);
}

/** x 좌우 반전(x → −x) */
export function mirrorRect(r: Rect): Rect { return rc(-r.x1, r.z0, -r.x0, r.z1); }
export function mirrorRects(list: readonly Rect[]): Rect[] { return list.map(mirrorRect); }
